<!-- Design System Preview Page -->
<div class="design-system-container">

  <!-- Header Section -->
  <div class="ds-header">
    <h1>LMC Design Guideline</h1>
    <p class="subtitle">A comprehensive guide to our Flat UI design guideline using Angular Material</p>
  </div>

  <!-- Navigation Tabs -->
  <mat-tab-group animationDuration="300ms">

    <!-- Color Palette Tab -->
    <mat-tab label="Color Palette">
      <div class="tab-content">
        <h2>Brand Colors</h2>
        <p>Our primary color palette is based on the company brand color (#83a1d1) with white text on buttons</p>

        <h4 class="mt-4">SCSS Variables Usage</h4>
        <pre class="code-block"><code>// Import variables in your component SCSS
&#64;import 'assets/styles/scss/variables';

// Use color variables
.my-element {{'{'}}
  color: $primary-500;        // Brand color #83a1d1
  background: $primary-50;    // Light background #f9fafd
  border: 1px solid $primary-300; // Border color
{{'}'}}</code></pre>

        <div class="color-grid">
          <div class="color-item">
            <div class="color-swatch primary-50"></div>
            <div class="color-info">
              <strong>Primary 50</strong>
              <code>#f9fafd</code>
            </div>
          </div>
          <div class="color-item">
            <div class="color-swatch primary-100"></div>
            <div class="color-info">
              <strong>Primary 100</strong>
              <code>#e1e8f4</code>
            </div>
          </div>
          <div class="color-item">
            <div class="color-swatch primary-300"></div>
            <div class="color-info">
              <strong>Primary 300</strong>
              <code>#b2c5e2</code>
            </div>
          </div>
          <div class="color-item highlight">
            <div class="color-swatch primary-500"></div>
            <div class="color-info">
              <strong>Primary 500 (Brand Color)</strong>
              <code>#83a1d1</code>
            </div>
          </div>
          <div class="color-item">
            <div class="color-swatch primary-700"></div>
            <div class="color-info">
              <strong>Primary 700</strong>
              <code>#5c7192</code>
            </div>
          </div>
          <div class="color-item">
            <div class="color-swatch primary-900"></div>
            <div class="color-info">
              <strong>Primary 900</strong>
              <code>#344054</code>
            </div>
          </div>
        </div>

        <h2 class="mt-4">Semantic Colors</h2>
        <p class="design-description">
          Semantic colors for success, warning, error states, and Material theme colors (accent/warn).
        </p>
        <div class="color-grid">
          <div class="color-item">
            <div class="color-swatch primary-500"></div>
            <div class="color-info">
              <strong>Primary</strong>
              <code>#83a1d1</code>
            </div>
          </div>
          <div class="color-item">
            <div class="color-swatch success-500"></div>
            <div class="color-info">
              <strong>Success</strong>
              <code>#4caf50</code>
            </div>
          </div>
          <div class="color-item">
            <div class="color-swatch warning-500"></div>
            <div class="color-info">
              <strong>Warning / Accent</strong>
              <code>#ff9800</code>
            </div>
          </div>
          <div class="color-item">
            <div class="color-swatch error-500"></div>
            <div class="color-info">
              <strong>Error / Warn</strong>
              <code>#f44336</code>
            </div>
          </div>
        </div>

        <h4 class="mt-4">Using Theme Colors in Components</h4>
        <pre class="code-block"><code>&lt;!-- Primary color (brand color #83a1d1) --&gt;
&lt;button mat-raised-button color="primary"&gt;Primary Button&lt;/button&gt;
&lt;mat-icon color="primary"&gt;check_circle&lt;/mat-icon&gt;
&lt;mat-progress-bar color="primary"&gt;&lt;/mat-progress-bar&gt;

&lt;!-- Accent color (orange #ff9800 - same as warning) --&gt;
&lt;button mat-raised-button color="accent"&gt;Accent Button&lt;/button&gt;
&lt;mat-icon color="accent"&gt;star&lt;/mat-icon&gt;

&lt;!-- Warn color (red #f44336 - for errors and destructive actions) --&gt;
&lt;button mat-raised-button color="warn"&gt;Delete&lt;/button&gt;
&lt;mat-icon color="warn"&gt;error&lt;/mat-icon&gt;</code></pre>
      </div>
    </mat-tab>

    <!-- Typography Tab -->
    <mat-tab label="Typography">
      <div class="tab-content">
        <h2>Typography System</h2>
        <p class="design-description">Our typography follows Material Design guidelines with Roboto font family.</p>

        <h4 class="mt-4">HTML Code Example</h4>
        <pre class="code-block"><code>&lt;h1&gt;Heading 1 - Main Page Title&lt;/h1&gt;
&lt;h2&gt;Heading 2 - Section Title&lt;/h2&gt;
&lt;h3&gt;Heading 3 - Subsection Title&lt;/h3&gt;
&lt;h4&gt;Heading 4 - Card Title&lt;/h4&gt;
&lt;h5&gt;Heading 5 - Small Card Title&lt;/h5&gt;
&lt;h6&gt;Heading 6 - Minor Heading&lt;/h6&gt;

&lt;p class="body-1"&gt;Primary body text (14px)&lt;/p&gt;
&lt;p class="body-2"&gt;Secondary body text&lt;/p&gt;
&lt;p class="caption"&gt;Caption text for hints and labels&lt;/p&gt;</code></pre>

        <h4 class="mt-4">Typography Classes</h4>
        <pre class="code-block"><code>// Available typography classes
.display-1 to .display-6   // Heading classes
.body-1                     // Primary body text (default)
.body-2                     // Secondary body text
.caption                    // Small text for hints/labels

// Font weights
font-weight: 300;  // Light (H1, H2)
font-weight: 400;  // Regular (H3, H4, body)
font-weight: 500;  // Medium (H5, H6)</code></pre>

        <div class="typography-section">
          <h1 class="display-1">Display 1 - Heading 1</h1>
          <h2 class="display-2">Display 2 - Heading 2</h2>
          <h3 class="display-3">Display 3 - Heading 3</h3>
          <h4 class="display-4">Display 4 - Heading 4</h4>
          <h5 class="display-5">Display 5 - Heading 5</h5>
          <h6 class="display-6">Display 6 - Heading 6</h6>

          <p class="body-1">
            <strong>Body 1:</strong> This is the primary body text style used throughout the application.
            It maintains optimal readability with a font size of 14px (0.875rem).
          </p>

          <p class="body-2">
            <strong>Body 2:</strong> Secondary body text for less prominent content.
          </p>

          <p class="caption">
            Caption: Used for hints, labels, and auxiliary information.
          </p>
        </div>
      </div>
    </mat-tab>

    <!-- Cards Tab -->
    <mat-tab label="Cards">
      <div class="tab-content">

        <!-- Design 1: Metric Cards (Flat with Border) -->
        <section class="design-section">
          <h2>Design 1: Flat Metric Cards</h2>
          <p class="design-description">
            Minimalist cards with subtle borders, perfect for displaying key metrics and statistics.
            Features: flat design, hover effects, icon support, and trend indicators.
          </p>

          <div class="card-row">
            <mat-card class="metric-card" *ngFor="let card of cardData">
              <mat-card-header>
                <mat-icon mat-card-avatar class="card-icon">{{card.icon}}</mat-icon>
                <mat-card-title>{{card.title}}</mat-card-title>
              </mat-card-header>
              <mat-card-content>
                <div class="metric-value">{{card.value}}</div>
                <div class="metric-trend" [class.positive]="card.trendPositive" [class.negative]="!card.trendPositive">
                  <mat-icon>{{ card.trendPositive ? 'trending_up' : 'trending_down' }}</mat-icon>
                  <span>{{card.trend}} from last month</span>
                </div>
              </mat-card-content>
            </mat-card>
          </div>

          <h4 class="mt-4">HTML Code</h4>
          <pre class="code-block"><code>&lt;mat-card class="metric-card"&gt;
  &lt;mat-card-header&gt;
    &lt;mat-icon mat-card-avatar class="card-icon"&gt;description&lt;/mat-icon&gt;
    &lt;mat-card-title&gt;Total Applications&lt;/mat-card-title&gt;
  &lt;/mat-card-header&gt;
  &lt;mat-card-content&gt;
    &lt;div class="metric-value"&gt;1,234&lt;/div&gt;
    &lt;div class="metric-trend positive"&gt;
      &lt;mat-icon&gt;trending_up&lt;/mat-icon&gt;
      &lt;span&gt;+12% from last month&lt;/span&gt;
    &lt;/div&gt;
  &lt;/mat-card-content&gt;
&lt;/mat-card&gt;</code></pre>
        </section>

        <!-- Design 2: Content Cards (Elevated with Actions) -->
        <section class="design-section">
          <h2>Design 2: Elevated Content Cards</h2>
          <p class="design-description">
            Cards with subtle elevation for content display. Includes header, content area, and action buttons.
            Ideal for forms, detailed information, and interactive content.
          </p>

          <div class="card-row">
            <mat-card class="content-card">
              <mat-card-header>
                <div mat-card-avatar class="avatar-icon">
                  <mat-icon>person</mat-icon>
                </div>
                <mat-card-title>Application Details</mat-card-title>
                <mat-card-subtitle>Loan ID: #LA-2024-001234</mat-card-subtitle>
              </mat-card-header>

              <mat-card-content>
                <div class="info-row">
                  <span class="label">Applicant Name:</span>
                  <span class="value">John Doe</span>
                </div>
                <div class="info-row">
                  <span class="label">Loan Amount:</span>
                  <span class="value">$50,000</span>
                </div>
                <div class="info-row">
                  <span class="label">Status:</span>
                  <span class="value">
                    <span class="status-badge status-approved">Approved</span>
                  </span>
                </div>
              </mat-card-content>

              <mat-card-actions align="end">
                <button mat-button color="primary">VIEW DETAILS</button>
                <button mat-raised-button color="primary">EDIT</button>
              </mat-card-actions>
            </mat-card>
          </div>

          <h4 class="mt-4">HTML Code</h4>
          <pre class="code-block"><code>&lt;mat-card class="content-card"&gt;
  &lt;mat-card-header&gt;
    &lt;div mat-card-avatar class="avatar-icon"&gt;
      &lt;mat-icon&gt;person&lt;/mat-icon&gt;
    &lt;/div&gt;
    &lt;mat-card-title&gt;Application Details&lt;/mat-card-title&gt;
    &lt;mat-card-subtitle&gt;Loan ID: #LA-2024-001234&lt;/mat-card-subtitle&gt;
  &lt;/mat-card-header&gt;

  &lt;mat-card-content&gt;
    &lt;div class="info-row"&gt;
      &lt;span class="label"&gt;Applicant Name:&lt;/span&gt;
      &lt;span class="value"&gt;John Doe&lt;/span&gt;
    &lt;/div&gt;
    &lt;div class="info-row"&gt;
      &lt;span class="label"&gt;Loan Amount:&lt;/span&gt;
      &lt;span class="value"&gt;$50,000&lt;/span&gt;
    &lt;/div&gt;
    &lt;div class="info-row"&gt;
      &lt;span class="label"&gt;Status:&lt;/span&gt;
      &lt;span class="value"&gt;
        &lt;span class="status-badge status-approved"&gt;Approved&lt;/span&gt;
      &lt;/span&gt;
    &lt;/div&gt;
  &lt;/mat-card-content&gt;

  &lt;mat-card-actions align="end"&gt;
    &lt;button mat-button color="primary"&gt;VIEW&lt;/button&gt;
    &lt;button mat-raised-button color="primary"&gt;EDIT&lt;/button&gt;
  &lt;/mat-card-actions&gt;
&lt;/mat-card&gt;</code></pre>
        </section>

        <!-- Card Variants -->
        <section class="design-section">
          <h2>Card Variants</h2>
          <p class="design-description">Different card states and variations for various use cases.</p>

          <h4 class="mt-4">HTML Code Examples</h4>
          <pre class="code-block"><code>&lt;!-- Simple Card (No header/actions) --&gt;
&lt;mat-card class="simple-card"&gt;
  &lt;mat-card-content&gt;
    &lt;h3&gt;Simple Card&lt;/h3&gt;
    &lt;p&gt;Minimal card with just content.&lt;/p&gt;
  &lt;/mat-card-content&gt;
&lt;/mat-card&gt;

&lt;!-- Card with Image --&gt;
&lt;mat-card class="image-card"&gt;
  &lt;mat-card-header&gt;
    &lt;mat-card-title&gt;Card with Image&lt;/mat-card-title&gt;
  &lt;/mat-card-header&gt;
  &lt;img mat-card-image src="path/to/image.jpg" alt="Description"&gt;
  &lt;mat-card-content&gt;
    &lt;p&gt;Card content goes here&lt;/p&gt;
  &lt;/mat-card-content&gt;
&lt;/mat-card&gt;

&lt;!-- Disabled Card --&gt;
&lt;mat-card class="disabled-card"&gt;
  &lt;mat-card-content&gt;
    &lt;h3&gt;Disabled State&lt;/h3&gt;
    &lt;p&gt;Card visually disabled&lt;/p&gt;
  &lt;/mat-card-content&gt;
&lt;/mat-card&gt;</code></pre>

          <div class="card-row">
            <!-- Simple Card -->
            <mat-card class="simple-card">
              <mat-card-content>
                <h3>Simple Card</h3>
                <p>Minimal card with just content, no header or actions.</p>
              </mat-card-content>
            </mat-card>

            <!-- Card with Image -->
            <mat-card class="image-card">
              <mat-card-header>
                <mat-card-title>Card with Image</mat-card-title>
                <mat-card-subtitle>Image placeholder support</mat-card-subtitle>
              </mat-card-header>
              <div class="card-image-placeholder">
                <mat-icon>image</mat-icon>
                <span>Image Area</span>
              </div>
              <mat-card-content>
                <p>Cards can include images for richer visual content.</p>
              </mat-card-content>
            </mat-card>

            <!-- Disabled Card -->
            <mat-card class="disabled-card">
              <mat-card-content>
                <h3>Disabled State</h3>
                <p>Cards can be visually disabled to indicate unavailable content.</p>
              </mat-card-content>
            </mat-card>
          </div>
        </section>

      </div>
    </mat-tab>

    <!-- Tables Tab -->
    <mat-tab label="Tables">
      <div class="tab-content">

        <section class="design-section">
          <h2>Material Design Table</h2>
          <p class="design-description">
            Flat table design with hover states, alternating rows, and action buttons.
            Follows Material Design guidelines with responsive layout support.
          </p>

          <!-- Table Design 1: Standard Material Table -->
          <div class="table-container">
            <h3>Standard Data Table</h3>
            <table mat-table [dataSource]="dataSource" class="flat-table">

              <!-- ID Column -->
              <ng-container matColumnDef="id">
                <th mat-header-cell *matHeaderCellDef> ID </th>
                <td mat-cell *matCellDef="let element"> {{element.id}} </td>
              </ng-container>

              <!-- Name Column -->
              <ng-container matColumnDef="name">
                <th mat-header-cell *matHeaderCellDef> Name </th>
                <td mat-cell *matCellDef="let element"> {{element.name}} </td>
              </ng-container>

              <!-- Email Column -->
              <ng-container matColumnDef="email">
                <th mat-header-cell *matHeaderCellDef> Email </th>
                <td mat-cell *matCellDef="let element"> {{element.email}} </td>
              </ng-container>

              <!-- Status Column -->
              <ng-container matColumnDef="status">
                <th mat-header-cell *matHeaderCellDef> Status </th>
                <td mat-cell *matCellDef="let element">
                  <span class="status-badge" [ngClass]="getStatusClass(element.status)">
                    {{element.status}}
                  </span>
                </td>
              </ng-container>

              <!-- Amount Column -->
              <ng-container matColumnDef="amount">
                <th mat-header-cell *matHeaderCellDef> Amount </th>
                <td mat-cell *matCellDef="let element"> ${{element.amount | number}} </td>
              </ng-container>

              <!-- Actions Column -->
              <ng-container matColumnDef="actions">
                <th mat-header-cell *matHeaderCellDef> Actions </th>
                <td mat-cell *matCellDef="let element">
                  <button mat-icon-button color="primary" (click)="onEdit(element)" matTooltip="Edit">
                    <mat-icon>edit</mat-icon>
                  </button>
                  <button mat-icon-button color="warn" (click)="onDelete(element)" matTooltip="Delete">
                    <mat-icon>delete</mat-icon>
                  </button>
                </td>
              </ng-container>

              <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
              <tr mat-row *matRowDef="let row; columns: displayedColumns;"></tr>
            </table>
          </div>

          <h4 class="mt-4">HTML Code Example</h4>
          <pre class="code-block"><code>&lt;mat-card class="content-card"&gt;
  &lt;mat-card-header&gt;
    &lt;mat-card-title&gt;Applications&lt;/mat-card-title&gt;
  &lt;/mat-card-header&gt;
  &lt;mat-card-content class="p-0"&gt;
    &lt;table mat-table [dataSource]="dataSource" class="flat-table"&gt;

      &lt;!-- Name Column --&gt;
      &lt;ng-container matColumnDef="name"&gt;
        &lt;th mat-header-cell *matHeaderCellDef&gt; Name &lt;/th&gt;
        &lt;td mat-cell *matCellDef="let element"&gt; {{'{{element.name}}'}} &lt;/td&gt;
      &lt;/ng-container&gt;

      &lt;!-- Status Column --&gt;
      &lt;ng-container matColumnDef="status"&gt;
        &lt;th mat-header-cell *matHeaderCellDef&gt; Status &lt;/th&gt;
        &lt;td mat-cell *matCellDef="let element"&gt;
          &lt;span class="status-badge" [ngClass]="'status-' + element.status.toLowerCase()"&gt;
            {{'{{element.status}}'}}
          &lt;/span&gt;
        &lt;/td&gt;
      &lt;/ng-container&gt;

      &lt;!-- Actions Column --&gt;
      &lt;ng-container matColumnDef="actions"&gt;
        &lt;th mat-header-cell *matHeaderCellDef&gt; Actions &lt;/th&gt;
        &lt;td mat-cell *matCellDef="let element"&gt;
          &lt;button mat-icon-button color="primary" matTooltip="Edit"&gt;
            &lt;mat-icon&gt;edit&lt;/mat-icon&gt;
          &lt;/button&gt;
          &lt;button mat-icon-button color="warn" matTooltip="Delete"&gt;
            &lt;mat-icon&gt;delete&lt;/mat-icon&gt;
          &lt;/button&gt;
        &lt;/td&gt;
      &lt;/ng-container&gt;

      &lt;tr mat-header-row *matHeaderRowDef="displayedColumns"&gt;&lt;/tr&gt;
      &lt;tr mat-row *matRowDef="let row; columns: displayedColumns;"&gt;&lt;/tr&gt;
    &lt;/table&gt;
  &lt;/mat-card-content&gt;
&lt;/mat-card&gt;</code></pre>

          <h4 class="mt-4">TypeScript Code Example</h4>
          <pre class="code-block"><code>export class ApplicationsComponent implements OnInit {{'{'}}
  displayedColumns: string[] = ['id', 'name', 'status', 'amount', 'actions'];
  dataSource: MatTableDataSource&lt;Application&gt;;

  ngOnInit() {{'{'}}
    const data: Application[] = [
      {{'{'}} id: 'LA-001', name: 'John Doe', status: 'Approved', amount: 50000 {{'}'}},
      {{'{'}} id: 'LA-002', name: 'Jane Smith', status: 'Pending', amount: 75000 {{'}'}}
    ];
    this.dataSource = new MatTableDataSource(data);
  {{'}'}}

  onEdit(element: Application) {{'{'}}
    // Handle edit action
  {{'}'}}

  onDelete(element: Application) {{'{'}}
    // Handle delete action
  {{'}'}}
{{'}'}}</code></pre>

        </section>

      </div>
    </mat-tab>

    <!-- Buttons Tab -->
    <mat-tab label="Buttons">
      <div class="tab-content">
        <h2>Button Components</h2>
        <p class="design-description">
          Material buttons with LMC theme colors. All raised and flat buttons automatically have white text
          when using <code>color="primary"</code>, <code>color="accent"</code>, or <code>color="warn"</code>.
        </p>

        <section class="design-section">
          <h3>Raised Buttons</h3>
          <p class="design-description">
            Elevated buttons with white text on colored backgrounds. Use for primary and secondary actions.
          </p>

          <div class="component-row">
            <button mat-raised-button color="primary">Primary Button</button>
            <button mat-raised-button color="accent">Accent Button</button>
            <button mat-raised-button color="warn">Warn Button</button>
            <button mat-raised-button>Default Button</button>
            <button mat-raised-button disabled>Disabled</button>
          </div>

          <h4 class="mt-4">HTML Code</h4>
          <pre class="code-block"><code>&lt;button mat-raised-button color="primary"&gt;Primary Button&lt;/button&gt;
&lt;button mat-raised-button color="accent"&gt;Accent Button&lt;/button&gt;
&lt;button mat-raised-button color="warn"&gt;Warn Button&lt;/button&gt;
&lt;button mat-raised-button&gt;Default Button&lt;/button&gt;
&lt;button mat-raised-button disabled&gt;Disabled&lt;/button&gt;</code></pre>
        </section>

        <section class="design-section">
          <h3>Flat Buttons</h3>
          <p class="design-description">
            Flat buttons without elevation, with white text on colored backgrounds.
          </p>

          <div class="component-row">
            <button mat-flat-button color="primary">Primary Flat</button>
            <button mat-flat-button color="accent">Accent Flat</button>
            <button mat-flat-button color="warn">Warn Flat</button>
            <button mat-flat-button>Default Flat</button>
            <button mat-flat-button disabled>Disabled</button>
          </div>

          <h4 class="mt-4">HTML Code</h4>
          <pre class="code-block"><code>&lt;button mat-flat-button color="primary"&gt;Primary Flat&lt;/button&gt;
&lt;button mat-flat-button color="accent"&gt;Accent Flat&lt;/button&gt;
&lt;button mat-flat-button color="warn"&gt;Warn Flat&lt;/button&gt;</code></pre>
        </section>

        <section class="design-section">
          <h3>Stroked Buttons (Outlined)</h3>
          <p class="design-description">
            Outlined buttons with transparent background and colored border.
          </p>

          <div class="component-row">
            <button mat-stroked-button color="primary">Outlined Primary</button>
            <button mat-stroked-button color="accent">Outlined Accent</button>
            <button mat-stroked-button color="warn">Outlined Warn</button>
            <button mat-stroked-button>Default Outlined</button>
            <button mat-stroked-button disabled>Disabled</button>
          </div>

          <h4 class="mt-4">HTML Code</h4>
          <pre class="code-block"><code>&lt;button mat-stroked-button color="primary"&gt;Outlined Primary&lt;/button&gt;
&lt;button mat-stroked-button color="accent"&gt;Outlined Accent&lt;/button&gt;
&lt;button mat-stroked-button color="warn"&gt;Outlined Warn&lt;/button&gt;</code></pre>
        </section>

        <section class="design-section">
          <h3>Text Buttons</h3>
          <p class="design-description">
            Minimal buttons with no background or border. Use for tertiary actions or in dialogs.
          </p>

          <div class="component-row">
            <button mat-button color="primary">Primary Text</button>
            <button mat-button color="accent">Accent Text</button>
            <button mat-button color="warn">Warn Text</button>
            <button mat-button>Default Text</button>
            <button mat-button disabled>Disabled</button>
          </div>

          <h4 class="mt-4">HTML Code</h4>
          <pre class="code-block"><code>&lt;button mat-button color="primary"&gt;Primary Text&lt;/button&gt;
&lt;button mat-button color="accent"&gt;Accent Text&lt;/button&gt;
&lt;button mat-button color="warn"&gt;Warn Text&lt;/button&gt;</code></pre>
        </section>

        <section class="design-section">
          <h3>Buttons with Icons</h3>
          <p class="design-description">
            Buttons with icons automatically have white icons when using primary, accent, or warn colors.
          </p>

          <div class="component-row">
            <button mat-raised-button color="primary">
              <mat-icon>save</mat-icon>
              Save
            </button>
            <button mat-raised-button color="primary">
              <mat-icon>check</mat-icon>
              Submit
            </button>
            <button mat-raised-button color="accent">
              <mat-icon>star</mat-icon>
              Favorite
            </button>
            <button mat-raised-button color="warn">
              <mat-icon>delete</mat-icon>
              Delete
            </button>
          </div>

          <h4 class="mt-4">HTML Code</h4>
          <pre class="code-block"><code>&lt;button mat-raised-button color="primary"&gt;
  &lt;mat-icon&gt;save&lt;/mat-icon&gt;
  Save
&lt;/button&gt;</code></pre>
        </section>

        <section class="design-section">
          <h3>Icon Buttons</h3>
          <p class="design-description">
            Circular buttons containing only an icon. Use for actions like edit, delete, close.
          </p>

          <div class="component-row">
            <button mat-icon-button color="primary" matTooltip="Edit">
              <mat-icon>edit</mat-icon>
            </button>
            <button mat-icon-button color="primary" matTooltip="Visibility">
              <mat-icon>visibility</mat-icon>
            </button>
            <button mat-icon-button color="accent" matTooltip="Favorite">
              <mat-icon>star</mat-icon>
            </button>
            <button mat-icon-button color="warn" matTooltip="Delete">
              <mat-icon>delete</mat-icon>
            </button>
            <button mat-icon-button matTooltip="More Options">
              <mat-icon>more_vert</mat-icon>
            </button>
            <button mat-icon-button disabled matTooltip="Disabled">
              <mat-icon>block</mat-icon>
            </button>
          </div>

          <h4 class="mt-4">HTML Code</h4>
          <pre class="code-block"><code>&lt;button mat-icon-button color="primary" matTooltip="Edit"&gt;
  &lt;mat-icon&gt;edit&lt;/mat-icon&gt;
&lt;/button&gt;
&lt;button mat-icon-button color="warn" matTooltip="Delete"&gt;
  &lt;mat-icon&gt;delete&lt;/mat-icon&gt;
&lt;/button&gt;</code></pre>
        </section>

        <section class="design-section">
          <h3>FAB Buttons (Floating Action)</h3>
          <p class="design-description">
            Floating action buttons for primary actions. Available in standard and mini sizes.
          </p>

          <div class="component-row">
            <button mat-fab color="primary" matTooltip="Add">
              <mat-icon>add</mat-icon>
            </button>
            <button mat-fab color="accent" matTooltip="Edit">
              <mat-icon>edit</mat-icon>
            </button>
            <button mat-fab color="warn" matTooltip="Delete">
              <mat-icon>delete</mat-icon>
            </button>
            <button mat-fab disabled matTooltip="Disabled">
              <mat-icon>block</mat-icon>
            </button>
          </div>

          <h4 class="mt-3">Mini FAB Buttons</h4>
          <div class="component-row">
            <button mat-mini-fab color="primary" matTooltip="Add">
              <mat-icon>add</mat-icon>
            </button>
            <button mat-mini-fab color="accent" matTooltip="Edit">
              <mat-icon>edit</mat-icon>
            </button>
            <button mat-mini-fab color="warn" matTooltip="Delete">
              <mat-icon>delete</mat-icon>
            </button>
          </div>

          <h4 class="mt-4">HTML Code</h4>
          <pre class="code-block"><code>&lt;button mat-fab color="primary"&gt;
  &lt;mat-icon&gt;add&lt;/mat-icon&gt;
&lt;/button&gt;
&lt;button mat-mini-fab color="primary"&gt;
  &lt;mat-icon&gt;add&lt;/mat-icon&gt;
&lt;/button&gt;</code></pre>
        </section>

        <section class="design-section">
          <h3>Best Practices</h3>

          <h4>DO's ✅</h4>
          <ul class="notes-list">
            <li>Use <code>color="primary"</code> for main actions and brand consistency</li>
            <li>Use <code>color="accent"</code> for secondary actions and highlights</li>
            <li>Use <code>color="warn"</code> only for destructive actions (delete, remove, etc.)</li>
            <li>Use <code>mat-raised-button</code> for primary CTAs (Call-to-Action)</li>
            <li>Use <code>mat-button</code> (text button) in dialogs for cancel/secondary actions</li>
            <li>Add <code>matTooltip</code> to icon buttons for accessibility</li>
            <li>Use <code>disabled</code> attribute to prevent interaction</li>
          </ul>

          <h4>DON'Ts ❌</h4>
          <ul class="notes-list">
            <li>Don't use multiple primary buttons in the same action group</li>
            <li>Don't use warn color for non-destructive actions</li>
            <li>Don't use icon buttons without tooltips</li>
            <li>Don't mix button styles in the same action group</li>
            <li>Don't create custom button styles - use Material buttons</li>
          </ul>
        </section>

      </div>
    </mat-tab>

    <!-- Form Inputs Tab -->
    <mat-tab label="Form Inputs">
      <div class="tab-content">
        <h2>Form Inputs</h2>
        <p class="design-description">
          Form inputs using Angular Material with outline appearance for consistency.
          All form fields follow the LMC design guideline with proper validation, error handling, and accessibility.
        </p>

        <!-- Text Input Section -->
        <section class="design-section">
          <h3>Text Inputs</h3>
          <p class="design-description">
            Standard text input fields with outline appearance. Use <code>matInput</code> directive with <code>mat-form-field</code>.
          </p>

          <div class="component-row">
            <mat-form-field appearance="outline" class="example-form-field">
              <mat-label>Full Name</mat-label>
              <input matInput placeholder="Enter your name">
            </mat-form-field>

            <mat-form-field appearance="outline" class="example-form-field">
              <mat-label>Email Address</mat-label>
              <input matInput type="email" placeholder="email@example.com">
              <mat-hint>We'll never share your email</mat-hint>
            </mat-form-field>

            <mat-form-field appearance="outline" class="example-form-field">
              <mat-label>Phone Number</mat-label>
              <input matInput type="tel" placeholder="(555) 123-4567">
              <mat-icon matPrefix>phone</mat-icon>
            </mat-form-field>
          </div>

          <h4 class="mt-4">HTML Code</h4>
          <pre class="code-block"><code>&lt;mat-form-field appearance="outline"&gt;
  &lt;mat-label&gt;Full Name&lt;/mat-label&gt;
  &lt;input matInput placeholder="Enter your name"&gt;
&lt;/mat-form-field&gt;

&lt;mat-form-field appearance="outline"&gt;
  &lt;mat-label&gt;Email Address&lt;/mat-label&gt;
  &lt;input matInput type="email" placeholder="email&#64;example.com"&gt;
  &lt;mat-hint&gt;We'll never share your email&lt;/mat-hint&gt;
&lt;/mat-form-field&gt;

&lt;mat-form-field appearance="outline"&gt;
  &lt;mat-label&gt;Phone Number&lt;/mat-label&gt;
  &lt;input matInput type="tel" placeholder="(555) 123-4567"&gt;
  &lt;mat-icon matPrefix&gt;phone&lt;/mat-icon&gt;
&lt;/mat-form-field&gt;</code></pre>
        </section>

        <!-- Number and Currency Inputs Section -->
        <section class="design-section">
          <h3>Number & Currency Inputs</h3>
          <p class="design-description">
            Numeric inputs with prefix/suffix for currency and units.
          </p>

          <div class="component-row">
            <mat-form-field appearance="outline" class="example-form-field">
              <mat-label>Loan Amount</mat-label>
              <input matInput type="number" placeholder="50000">
              <span matPrefix>$&nbsp;</span>
            </mat-form-field>

            <mat-form-field appearance="outline" class="example-form-field">
              <mat-label>Interest Rate</mat-label>
              <input matInput type="number" step="0.01" placeholder="5.25">
              <span matSuffix>&nbsp;%</span>
            </mat-form-field>

            <mat-form-field appearance="outline" class="example-form-field">
              <mat-label>Loan Term</mat-label>
              <input matInput type="number" placeholder="24">
              <span matSuffix>&nbsp;months</span>
            </mat-form-field>
          </div>

          <h4 class="mt-4">HTML Code</h4>
          <pre class="code-block"><code>&lt;mat-form-field appearance="outline"&gt;
  &lt;mat-label&gt;Loan Amount&lt;/mat-label&gt;
  &lt;input matInput type="number" placeholder="50000"&gt;
  &lt;span matPrefix&gt;$&amp;nbsp;&lt;/span&gt;
&lt;/mat-form-field&gt;

&lt;mat-form-field appearance="outline"&gt;
  &lt;mat-label&gt;Interest Rate&lt;/mat-label&gt;
  &lt;input matInput type="number" step="0.01" placeholder="5.25"&gt;
  &lt;span matSuffix&gt;&amp;nbsp;%&lt;/span&gt;
&lt;/mat-form-field&gt;</code></pre>
        </section>

        <!-- Textarea Section -->
        <section class="design-section">
          <h3>Textarea</h3>
          <p class="design-description">
            Multi-line text input for longer content like comments or descriptions.
          </p>

          <div class="component-row">
            <mat-form-field appearance="outline" class="example-form-field-wide">
              <mat-label>Comments</mat-label>
              <textarea matInput rows="4" placeholder="Enter your comments here..."></textarea>
              <mat-hint align="end">0 / 500</mat-hint>
            </mat-form-field>
          </div>

          <h4 class="mt-4">HTML Code</h4>
          <pre class="code-block"><code>&lt;mat-form-field appearance="outline"&gt;
  &lt;mat-label&gt;Comments&lt;/mat-label&gt;
  &lt;textarea matInput rows="4" placeholder="Enter your comments here..."&gt;&lt;/textarea&gt;
  &lt;mat-hint align="end"&gt;0 / 500&lt;/mat-hint&gt;
&lt;/mat-form-field&gt;</code></pre>
        </section>

        <!-- Select Dropdown Section -->
        <section class="design-section">
          <h3>Select Dropdown</h3>
          <p class="design-description">
            Dropdown select fields for choosing from predefined options.
          </p>

          <div class="component-row">
            <mat-form-field appearance="outline" class="example-form-field">
              <mat-label>Loan Status</mat-label>
              <mat-select placeholder="Select status">
                <mat-option value="active">Active</mat-option>
                <mat-option value="pending">Pending</mat-option>
                <mat-option value="approved">Approved</mat-option>
                <mat-option value="rejected">Rejected</mat-option>
              </mat-select>
            </mat-form-field>

            <mat-form-field appearance="outline" class="example-form-field">
              <mat-label>Priority</mat-label>
              <mat-select>
                <mat-option value="low">
                  <mat-icon color="primary">arrow_downward</mat-icon> Low
                </mat-option>
                <mat-option value="medium">
                  <mat-icon color="accent">drag_handle</mat-icon> Medium
                </mat-option>
                <mat-option value="high">
                  <mat-icon color="warn">arrow_upward</mat-icon> High
                </mat-option>
              </mat-select>
            </mat-form-field>
          </div>

          <h4 class="mt-4">HTML Code</h4>
          <pre class="code-block"><code>&lt;mat-form-field appearance="outline"&gt;
  &lt;mat-label&gt;Loan Status&lt;/mat-label&gt;
  &lt;mat-select placeholder="Select status"&gt;
    &lt;mat-option value="active"&gt;Active&lt;/mat-option&gt;
    &lt;mat-option value="pending"&gt;Pending&lt;/mat-option&gt;
    &lt;mat-option value="approved"&gt;Approved&lt;/mat-option&gt;
  &lt;/mat-select&gt;
&lt;/mat-form-field&gt;

&lt;mat-form-field appearance="outline"&gt;
  &lt;mat-label&gt;Priority&lt;/mat-label&gt;
  &lt;mat-select&gt;
    &lt;mat-option value="low"&gt;
      &lt;mat-icon color="primary"&gt;arrow_downward&lt;/mat-icon&gt; Low
    &lt;/mat-option&gt;
    &lt;mat-option value="high"&gt;
      &lt;mat-icon color="warn"&gt;arrow_upward&lt;/mat-icon&gt; High
    &lt;/mat-option&gt;
  &lt;/mat-select&gt;
&lt;/mat-form-field&gt;</code></pre>
        </section>

        <!-- Date Picker Section -->
        <section class="design-section">
          <h3>Date Picker</h3>
          <p class="design-description">
            Date selection with calendar popup using Material Datepicker.
          </p>

          <div class="component-row">
            <mat-form-field appearance="outline" class="example-form-field">
              <mat-label>Application Date</mat-label>
              <input matInput [matDatepicker]="picker1" placeholder="MM/DD/YYYY">
              <mat-datepicker-toggle matSuffix [for]="picker1"></mat-datepicker-toggle>
              <mat-datepicker #picker1></mat-datepicker>
            </mat-form-field>

            <mat-form-field appearance="outline" class="example-form-field">
              <mat-label>Due Date</mat-label>
              <input matInput [matDatepicker]="picker2" placeholder="MM/DD/YYYY">
              <mat-datepicker-toggle matSuffix [for]="picker2">
                <mat-icon matDatepickerToggleIcon>event</mat-icon>
              </mat-datepicker-toggle>
              <mat-datepicker #picker2></mat-datepicker>
            </mat-form-field>
          </div>

          <h4 class="mt-4">HTML Code</h4>
          <pre class="code-block"><code>&lt;mat-form-field appearance="outline"&gt;
  &lt;mat-label&gt;Application Date&lt;/mat-label&gt;
  &lt;input matInput [matDatepicker]="picker" placeholder="MM/DD/YYYY"&gt;
  &lt;mat-datepicker-toggle matSuffix [for]="picker"&gt;&lt;/mat-datepicker-toggle&gt;
  &lt;mat-datepicker #picker&gt;&lt;/mat-datepicker&gt;
&lt;/mat-form-field&gt;

&lt;mat-form-field appearance="outline"&gt;
  &lt;mat-label&gt;Due Date&lt;/mat-label&gt;
  &lt;input matInput [matDatepicker]="picker2" placeholder="MM/DD/YYYY"&gt;
  &lt;mat-datepicker-toggle matSuffix [for]="picker2"&gt;
    &lt;mat-icon matDatepickerToggleIcon&gt;event&lt;/mat-icon&gt;
  &lt;/mat-datepicker-toggle&gt;
  &lt;mat-datepicker #picker2&gt;&lt;/mat-datepicker&gt;
&lt;/mat-form-field&gt;</code></pre>
        </section>

        <!-- Checkboxes Section -->
        <section class="design-section">
          <h3>Checkboxes</h3>
          <p class="design-description">
            Checkboxes for multiple selections and boolean values.
          </p>

          <div class="component-row" style="flex-direction: column; align-items: flex-start;">
            <mat-checkbox color="primary">I agree to the terms and conditions</mat-checkbox>
            <mat-checkbox color="primary" class="mt-2">Subscribe to newsletter</mat-checkbox>
            <mat-checkbox color="primary" class="mt-2" checked>Enable notifications</mat-checkbox>
            <mat-checkbox color="primary" class="mt-2" disabled>Disabled option</mat-checkbox>
          </div>

          <h4 class="mt-4">HTML Code</h4>
          <pre class="code-block"><code>&lt;mat-checkbox color="primary"&gt;I agree to the terms and conditions&lt;/mat-checkbox&gt;
&lt;mat-checkbox color="primary" checked&gt;Enable notifications&lt;/mat-checkbox&gt;
&lt;mat-checkbox color="primary" disabled&gt;Disabled option&lt;/mat-checkbox&gt;</code></pre>
        </section>

        <!-- Radio Buttons Section -->
        <section class="design-section">
          <h3>Radio Buttons</h3>
          <p class="design-description">
            Radio buttons for single selection from multiple options.
          </p>

          <div class="component-row" style="flex-direction: column; align-items: flex-start;">
            <label class="form-section-label">Gender</label>
            <mat-radio-group color="primary">
              <mat-radio-button value="male" class="mr-3">Male</mat-radio-button>
              <mat-radio-button value="female" class="mr-3">Female</mat-radio-button>
              <mat-radio-button value="other">Other</mat-radio-button>
            </mat-radio-group>
          </div>

          <div class="component-row mt-3" style="flex-direction: column; align-items: flex-start;">
            <label class="form-section-label">Loan Type</label>
            <mat-radio-group color="primary">
              <mat-radio-button value="personal" class="d-block mb-2">Personal Loan</mat-radio-button>
              <mat-radio-button value="business" class="d-block mb-2">Business Loan</mat-radio-button>
              <mat-radio-button value="mortgage" class="d-block mb-2">Mortgage</mat-radio-button>
            </mat-radio-group>
          </div>

          <h4 class="mt-4">HTML Code</h4>
          <pre class="code-block"><code>&lt;mat-radio-group color="primary"&gt;
  &lt;mat-radio-button value="male"&gt;Male&lt;/mat-radio-button&gt;
  &lt;mat-radio-button value="female"&gt;Female&lt;/mat-radio-button&gt;
  &lt;mat-radio-button value="other"&gt;Other&lt;/mat-radio-button&gt;
&lt;/mat-radio-group&gt;

&lt;mat-radio-group color="primary"&gt;
  &lt;mat-radio-button value="personal" class="d-block mb-2"&gt;Personal Loan&lt;/mat-radio-button&gt;
  &lt;mat-radio-button value="business" class="d-block mb-2"&gt;Business Loan&lt;/mat-radio-button&gt;
  &lt;mat-radio-button value="mortgage" class="d-block mb-2"&gt;Mortgage&lt;/mat-radio-button&gt;
&lt;/mat-radio-group&gt;</code></pre>
        </section>

        <!-- Slide Toggles Section -->
        <section class="design-section">
          <h3>Slide Toggles</h3>
          <p class="design-description">
            Toggle switches for on/off states and feature enablement.
          </p>

          <div class="component-row" style="flex-direction: column; align-items: flex-start;">
            <mat-slide-toggle color="primary">Enable auto-payment</mat-slide-toggle>
            <mat-slide-toggle color="primary" class="mt-2" checked>Email notifications</mat-slide-toggle>
            <mat-slide-toggle color="primary" class="mt-2">SMS alerts</mat-slide-toggle>
            <mat-slide-toggle color="primary" class="mt-2" disabled>Disabled toggle</mat-slide-toggle>
          </div>

          <h4 class="mt-4">HTML Code</h4>
          <pre class="code-block"><code>&lt;mat-slide-toggle color="primary"&gt;Enable auto-payment&lt;/mat-slide-toggle&gt;
&lt;mat-slide-toggle color="primary" checked&gt;Email notifications&lt;/mat-slide-toggle&gt;
&lt;mat-slide-toggle color="primary" disabled&gt;Disabled toggle&lt;/mat-slide-toggle&gt;</code></pre>
        </section>

        <!-- Form Validation Section -->
        <section class="design-section">
          <h3>Form Validation</h3>
          <p class="design-description">
            Form fields with validation states and error messages.
          </p>

          <div class="component-row">
            <mat-form-field appearance="outline" class="example-form-field">
              <mat-label>Email (Required)</mat-label>
              <input matInput type="email" required>
              <mat-error>Please enter a valid email address</mat-error>
            </mat-form-field>

            <mat-form-field appearance="outline" class="example-form-field">
              <mat-label>Password (Min 8 chars)</mat-label>
              <input matInput type="password" minlength="8" required>
              <mat-error>Password must be at least 8 characters</mat-error>
            </mat-form-field>

            <mat-form-field appearance="outline" class="example-form-field">
              <mat-label>Amount (Required)</mat-label>
              <input matInput type="number" required min="1000" max="100000">
              <span matPrefix>$&nbsp;</span>
              <mat-error>Amount must be between $1,000 and $100,000</mat-error>
            </mat-form-field>
          </div>

          <h4 class="mt-4">HTML Code</h4>
          <pre class="code-block"><code>&lt;mat-form-field appearance="outline"&gt;
  &lt;mat-label&gt;Email (Required)&lt;/mat-label&gt;
  &lt;input matInput type="email" required&gt;
  &lt;mat-error&gt;Please enter a valid email address&lt;/mat-error&gt;
&lt;/mat-form-field&gt;

&lt;mat-form-field appearance="outline"&gt;
  &lt;mat-label&gt;Email&lt;/mat-label&gt;
  &lt;input matInput formControlName="email" required&gt;
  &lt;mat-error&gt;Email is required&lt;/mat-error&gt;
&lt;/mat-form-field&gt;</code></pre>
        </section>

        <!-- Complete Form Example Section -->
        <section class="design-section">
          <h3>Complete Form Example</h3>
          <p class="design-description">
            A complete form demonstrating multiple input types working together.
          </p>

          <mat-card class="content-card">
            <mat-card-header>
              <mat-card-title>Loan Application Form</mat-card-title>
            </mat-card-header>
            <mat-card-content>
              <div class="form-row">
                <mat-form-field appearance="outline" class="form-field-half">
                  <mat-label>First Name</mat-label>
                  <input matInput placeholder="John" required>
                  <mat-error>First name is required</mat-error>
                </mat-form-field>

                <mat-form-field appearance="outline" class="form-field-half">
                  <mat-label>Last Name</mat-label>
                  <input matInput placeholder="Doe" required>
                  <mat-error>Last name is required</mat-error>
                </mat-form-field>
              </div>

              <div class="form-row">
                <mat-form-field appearance="outline" class="form-field-full">
                  <mat-label>Email Address</mat-label>
                  <input matInput type="email" placeholder="john.doe@example.com" required>
                  <mat-icon matPrefix>email</mat-icon>
                  <mat-error>Valid email is required</mat-error>
                </mat-form-field>
              </div>

              <div class="form-row">
                <mat-form-field appearance="outline" class="form-field-half">
                  <mat-label>Phone Number</mat-label>
                  <input matInput type="tel" placeholder="(555) 123-4567" required>
                  <mat-icon matPrefix>phone</mat-icon>
                </mat-form-field>

                <mat-form-field appearance="outline" class="form-field-half">
                  <mat-label>Loan Amount</mat-label>
                  <input matInput type="number" placeholder="50000" required>
                  <span matPrefix>$&nbsp;</span>
                  <mat-error>Amount is required</mat-error>
                </mat-form-field>
              </div>

              <div class="form-row">
                <mat-form-field appearance="outline" class="form-field-half">
                  <mat-label>Loan Type</mat-label>
                  <mat-select placeholder="Select type" required>
                    <mat-option value="personal">Personal Loan</mat-option>
                    <mat-option value="business">Business Loan</mat-option>
                    <mat-option value="mortgage">Mortgage</mat-option>
                  </mat-select>
                  <mat-error>Loan type is required</mat-error>
                </mat-form-field>

                <mat-form-field appearance="outline" class="form-field-half">
                  <mat-label>Application Date</mat-label>
                  <input matInput [matDatepicker]="applicationPicker" placeholder="MM/DD/YYYY">
                  <mat-datepicker-toggle matSuffix [for]="applicationPicker"></mat-datepicker-toggle>
                  <mat-datepicker #applicationPicker></mat-datepicker>
                </mat-form-field>
              </div>

              <div class="form-row">
                <mat-form-field appearance="outline" class="form-field-full">
                  <mat-label>Additional Notes</mat-label>
                  <textarea matInput rows="3" placeholder="Enter any additional information..."></textarea>
                </mat-form-field>
              </div>

              <div class="form-row">
                <mat-checkbox color="primary">I agree to the terms and conditions</mat-checkbox>
              </div>

              <div class="form-row">
                <mat-slide-toggle color="primary">Enable email notifications</mat-slide-toggle>
              </div>
            </mat-card-content>
            <mat-card-actions align="end">
              <button mat-button>Cancel</button>
              <button mat-raised-button color="primary">Submit Application</button>
            </mat-card-actions>
          </mat-card>

          <h4 class="mt-4">Complete Form Code</h4>
          <pre class="code-block"><code>&lt;mat-card class="content-card"&gt;
  &lt;mat-card-header&gt;
    &lt;mat-card-title&gt;Loan Application Form&lt;/mat-card-title&gt;
  &lt;/mat-card-header&gt;
  &lt;mat-card-content&gt;
    &lt;!-- Two column layout --&gt;
    &lt;div class="form-row"&gt;
      &lt;mat-form-field appearance="outline" class="form-field-half"&gt;
        &lt;mat-label&gt;First Name&lt;/mat-label&gt;
        &lt;input matInput placeholder="John" required&gt;
      &lt;/mat-form-field&gt;

      &lt;mat-form-field appearance="outline" class="form-field-half"&gt;
        &lt;mat-label&gt;Last Name&lt;/mat-label&gt;
        &lt;input matInput placeholder="Doe" required&gt;
      &lt;/mat-form-field&gt;
    &lt;/div&gt;

    &lt;!-- Full width field --&gt;
    &lt;div class="form-row"&gt;
      &lt;mat-form-field appearance="outline" class="form-field-full"&gt;
        &lt;mat-label&gt;Email Address&lt;/mat-label&gt;
        &lt;input matInput type="email" required&gt;
        &lt;mat-icon matPrefix&gt;email&lt;/mat-icon&gt;
      &lt;/mat-form-field&gt;
    &lt;/div&gt;
  &lt;/mat-card-content&gt;
  &lt;mat-card-actions align="end"&gt;
    &lt;button mat-button&gt;Cancel&lt;/button&gt;
    &lt;button mat-raised-button color="primary"&gt;Submit&lt;/button&gt;
  &lt;/mat-card-actions&gt;
&lt;/mat-card&gt;</code></pre>

          <h4 class="mt-4">Required CSS for Form Layout</h4>
          <pre class="code-block"><code>// Form row - contains form fields
.form-row {{'{'}}
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
{{'}'}}

// Half width field (for 2-column layout)
.form-field-half {{'{'}}
  flex: 1;
  min-width: 250px;
{{'}'}}

// Full width field
.form-field-full {{'{'}}
  width: 100%;
{{'}'}}

// Optional: Consistent form field styling
mat-form-field {{'{'}}
  width: 100%;
{{'}'}}</code></pre>
        </section>

        <!-- Best Practices Section -->
        <section class="design-section">
          <h3>Best Practices</h3>

          <h4>DO's ✅</h4>
          <ul class="notes-list">
            <li>Always use <code>appearance="outline"</code> for consistency with LMC design guideline</li>
            <li>Use <code>color="primary"</code> for checkboxes, radio buttons, and slide toggles</li>
            <li>Provide clear labels with <code>&lt;mat-label&gt;</code> for all form fields</li>
            <li>Add helpful hints with <code>&lt;mat-hint&gt;</code> when needed</li>
            <li>Use <code>&lt;mat-error&gt;</code> for validation error messages</li>
            <li>Include placeholder text to guide users</li>
            <li>Use prefix/suffix for currency symbols and units (<code>matPrefix</code>, <code>matSuffix</code>)</li>
            <li>Add icons to enhance usability (<code>matPrefix</code> for input icons)</li>
            <li>Group related fields together visually</li>
            <li>Use appropriate input types (<code>email</code>, <code>tel</code>, <code>number</code>, etc.)</li>
            <li>Implement proper form validation with Angular Reactive Forms</li>
          </ul>

          <h4>DON'Ts ❌</h4>
          <ul class="notes-list">
            <li>Don't use different appearances - stick to <code>outline</code> only</li>
            <li>Don't omit labels - every field should have a clear label</li>
            <li>Don't forget to add validation and error messages</li>
            <li>Don't use generic error messages - be specific about what's wrong</li>
            <li>Don't overcrowd forms - use proper spacing and grouping</li>
            <li>Don't forget disabled states for non-editable fields</li>
            <li>Don't mix native HTML inputs with Material inputs</li>
          </ul>
        </section>

        <!-- TypeScript Example Section -->
        <section class="design-section">
          <h3>TypeScript Integration (Reactive Forms)</h3>
          <p class="design-description">
            Example of integrating form inputs with Angular Reactive Forms.
          </p>

          <pre class="code-block"><code>// Component TypeScript
import {{'{'}} Component, OnInit {{'}'}} from '&#64;angular/core';
import {{'{'}} FormBuilder, FormGroup, Validators {{'}'}} from '&#64;angular/forms';

export class LoanApplicationComponent implements OnInit {{'{'}}
  applicationForm: FormGroup;

  constructor(private fb: FormBuilder) {{'{'}} {{'}'}}

  ngOnInit() {{'{'}}
    this.applicationForm = this.fb.group({{'{'}}
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      phone: ['', Validators.required],
      loanAmount: ['', [Validators.required, Validators.min(1000)]],
      loanType: ['', Validators.required],
      applicationDate: [''],
      notes: [''],
      agreeTerms: [false, Validators.requiredTrue],
      emailNotifications: [true]
    {{'}'}});
  {{'}'}}

  onSubmit() {{'{'}}
    if (this.applicationForm.valid) {{'{'}}
      console.log('Form submitted:', this.applicationForm.value);
      // Submit form data to API
    {{'}'}} else {{'{'}}
      // Mark all fields as touched to show errors
      this.applicationForm.markAllAsTouched();
    {{'}'}}
  {{'}'}}

  // Getter for easy access to form controls
  get f() {{'{'}} return this.applicationForm.controls; {{'}'}}
{{'}'}}</code></pre>

          <h4 class="mt-4">HTML with FormGroup</h4>
          <pre class="code-block"><code>&lt;form [formGroup]="applicationForm" (ngSubmit)="onSubmit()"&gt;
  &lt;mat-form-field appearance="outline"&gt;
    &lt;mat-label&gt;First Name&lt;/mat-label&gt;
    &lt;input matInput formControlName="firstName" required&gt;
    &lt;mat-error *ngIf="f['firstName'].hasError('required')"&gt;
      First name is required
    &lt;/mat-error&gt;
  &lt;/mat-form-field&gt;

  &lt;mat-form-field appearance="outline"&gt;
    &lt;mat-label&gt;Email&lt;/mat-label&gt;
    &lt;input matInput formControlName="email" type="email" required&gt;
    &lt;mat-error *ngIf="f['email'].hasError('required')"&gt;
      Email is required
    &lt;/mat-error&gt;
    &lt;mat-error *ngIf="f['email'].hasError('email')"&gt;
      Invalid email format
    &lt;/mat-error&gt;
  &lt;/mat-form-field&gt;

  &lt;mat-checkbox formControlName="agreeTerms" color="primary"&gt;
    I agree to the terms
  &lt;/mat-checkbox&gt;

  &lt;button mat-raised-button color="primary" type="submit"
          [disabled]="applicationForm.invalid"&gt;
    Submit
  &lt;/button&gt;
&lt;/form&gt;</code></pre>
        </section>

      </div>
    </mat-tab>

    <!-- Icons Tab -->
    <mat-tab label="Icons">
      <div class="tab-content">
        <h2>Material Icons</h2>
        <p class="design-description">
          Material Design icons with theme colors. Use <code>color="primary"</code>, <code>color="accent"</code>,
          or <code>color="warn"</code> to apply theme colors.
        </p>

        <section class="design-section">
          <h3>Icon Colors</h3>

          <div class="theme-icons-grid">
            <div class="theme-icon-item">
              <mat-icon color="primary" class="large-icon">check_circle</mat-icon>
              <p><code>color="primary"</code></p>
              <p>Brand Color</p>
            </div>
            <div class="theme-icon-item">
              <mat-icon color="accent" class="large-icon">star</mat-icon>
              <p><code>color="accent"</code></p>
              <p>Accent Color</p>
            </div>
            <div class="theme-icon-item">
              <mat-icon color="warn" class="large-icon">error</mat-icon>
              <p><code>color="warn"</code></p>
              <p>Warn Color</p>
            </div>
            <div class="theme-icon-item">
              <mat-icon class="large-icon">info</mat-icon>
              <p>No color attribute</p>
              <p>Default Gray</p>
            </div>
          </div>

          <h4 class="mt-4">HTML Code</h4>
          <pre class="code-block"><code>&lt;mat-icon color="primary"&gt;check_circle&lt;/mat-icon&gt;
&lt;mat-icon color="accent"&gt;star&lt;/mat-icon&gt;
&lt;mat-icon color="warn"&gt;error&lt;/mat-icon&gt;
&lt;mat-icon&gt;info&lt;/mat-icon&gt;</code></pre>
        </section>

        <section class="design-section">
          <h3>Common Icons</h3>
          <p class="design-description">
            Frequently used icons in the LMCredit application.
          </p>

          <h4 class="mt-4">HTML Code</h4>
          <pre class="code-block"><code>&lt;!-- Standard icons with primary color --&gt;
&lt;mat-icon color="primary"&gt;home&lt;/mat-icon&gt;
&lt;mat-icon color="primary"&gt;person&lt;/mat-icon&gt;
&lt;mat-icon color="primary"&gt;description&lt;/mat-icon&gt;

&lt;!-- Warning/Error icons with warn color --&gt;
&lt;mat-icon color="warn"&gt;delete&lt;/mat-icon&gt;
&lt;mat-icon color="warn"&gt;warning&lt;/mat-icon&gt;
&lt;mat-icon color="warn"&gt;error&lt;/mat-icon&gt;

&lt;!-- Accent icons --&gt;
&lt;mat-icon color="accent"&gt;star&lt;/mat-icon&gt;
&lt;mat-icon color="accent"&gt;favorite&lt;/mat-icon&gt;</code></pre>

          <h4 class="mt-4">Icon Reference</h4>
          <pre class="code-block"><code>// Common LMCredit Icons
home           - Home/Dashboard
person         - User profile
description    - Documents/Applications
check_circle   - Success/Approved
attach_money   - Financial/Payments
edit           - Edit actions
delete         - Delete actions (use color="warn")
visibility     - View/Preview
warning        - Warnings (use color="warn")
settings       - Settings/Configuration
search         - Search functionality
add            - Add new items
close          - Close/Dismiss
more_vert      - More options menu</code></pre>

          <div class="component-row icon-row">
            <div class="icon-item">
              <mat-icon color="primary">home</mat-icon>
              <span>home</span>
            </div>
            <div class="icon-item">
              <mat-icon color="primary">person</mat-icon>
              <span>person</span>
            </div>
            <div class="icon-item">
              <mat-icon color="primary">description</mat-icon>
              <span>description</span>
            </div>
            <div class="icon-item">
              <mat-icon color="primary">check_circle</mat-icon>
              <span>check_circle</span>
            </div>
            <div class="icon-item">
              <mat-icon color="primary">attach_money</mat-icon>
              <span>attach_money</span>
            </div>
            <div class="icon-item">
              <mat-icon color="primary">edit</mat-icon>
              <span>edit</span>
            </div>
            <div class="icon-item">
              <mat-icon color="warn">delete</mat-icon>
              <span>delete</span>
            </div>
            <div class="icon-item">
              <mat-icon color="primary">visibility</mat-icon>
              <span>visibility</span>
            </div>
            <div class="icon-item">
              <mat-icon color="warn">warning</mat-icon>
              <span>warning</span>
            </div>
            <div class="icon-item">
              <mat-icon color="primary">settings</mat-icon>
              <span>settings</span>
            </div>
          </div>
        </section>

        <section class="design-section">
          <h3>Icon Sizes</h3>
          <p class="design-description">
            Icons can be sized using CSS or inline styles. Default size is 24px.
          </p>

          <div class="component-row" style="align-items: center;">
            <mat-icon color="primary" style="font-size: 18px; width: 18px; height: 18px;">star</mat-icon>
            <span class="ml-2">18px (Small)</span>
            <mat-icon color="primary" class="ml-4">star</mat-icon>
            <span class="ml-2">24px (Default)</span>
            <mat-icon color="primary" class="ml-4" style="font-size: 36px; width: 36px; height: 36px;">star</mat-icon>
            <span class="ml-2">36px (Large)</span>
            <mat-icon color="primary" class="ml-4" style="font-size: 48px; width: 48px; height: 48px;">star</mat-icon>
            <span class="ml-2">48px (Extra Large)</span>
          </div>

          <h4 class="mt-4">HTML Code</h4>
          <pre class="code-block"><code>&lt;mat-icon color="primary" style="font-size: 36px; width: 36px; height: 36px;"&gt;
  star
&lt;/mat-icon&gt;</code></pre>
        </section>

        <section class="design-section">
          <h3>Best Practices</h3>

          <h4>DO's ✅</h4>
          <ul class="notes-list">
            <li>Use <code>color="primary"</code> for standard action icons</li>
            <li>Use <code>color="warn"</code> for destructive action icons (delete, error, warning)</li>
            <li>Maintain consistent icon sizes within the same context</li>
            <li>Use semantic icon names that match their purpose</li>
            <li>Reference Material Icons: <a href="https://fonts.google.com/icons" target="_blank">https://fonts.google.com/icons</a></li>
          </ul>

          <h4>DON'Ts ❌</h4>
          <ul class="notes-list">
            <li>Don't use icons without tooltips when used as standalone buttons</li>
            <li>Don't mix outlined and filled icon styles</li>
            <li>Don't use overly complex or ambiguous icons</li>
            <li>Don't forget to set width and height when changing font-size</li>
          </ul>
        </section>

      </div>
    </mat-tab>

    <!-- Badges Tab -->
    <mat-tab label="Badges">
      <div class="tab-content">
        <h2>Status Badges</h2>
        <p class="design-description">
          Consistent status badges for displaying states across the application.
        </p>

        <section class="design-section">
          <h3>Standard Status Badges</h3>

          <div class="component-row">
            <span class="status-badge status-active">Active</span>
            <span class="status-badge status-pending">Pending</span>
            <span class="status-badge status-approved">Approved</span>
            <span class="status-badge status-rejected">Rejected</span>
          </div>

          <h4 class="mt-4">HTML Code</h4>
          <pre class="code-block"><code>&lt;span class="status-badge status-active"&gt;Active&lt;/span&gt;
&lt;span class="status-badge status-pending"&gt;Pending&lt;/span&gt;
&lt;span class="status-badge status-approved"&gt;Approved&lt;/span&gt;
&lt;span class="status-badge status-rejected"&gt;Rejected&lt;/span&gt;</code></pre>
        </section>

        <section class="design-section">
          <h3>Badge Styles</h3>

          <div class="info-row mb-3">
            <span class="label"><code>.status-active</code></span>
            <span class="value">Green - For active/enabled states</span>
          </div>
          <div class="info-row mb-3">
            <span class="label"><code>.status-pending</code></span>
            <span class="value">Orange - For pending/in-progress states</span>
          </div>
          <div class="info-row mb-3">
            <span class="label"><code>.status-approved</code></span>
            <span class="value">Blue/Green - For approved/completed states</span>
          </div>
          <div class="info-row mb-3">
            <span class="label"><code>.status-rejected</code></span>
            <span class="value">Red - For rejected/error states</span>
          </div>
        </section>

        <section class="design-section">
          <h3>Best Practices</h3>

          <h4>DO's ✅</h4>
          <ul class="notes-list">
            <li>Use consistent status names across the application</li>
            <li>Keep badge text short and clear (1-2 words)</li>
            <li>Use UPPERCASE text for emphasis</li>
            <li>Choose badge color based on semantic meaning</li>
          </ul>

          <h4>DON'Ts ❌</h4>
          <ul class="notes-list">
            <li>Don't use badges for long text (use chips instead)</li>
            <li>Don't create custom badge styles without updating the design guideline</li>
            <li>Don't use ambiguous status names</li>
          </ul>
        </section>

      </div>
    </mat-tab>

    <!-- Progress Tab -->
    <mat-tab label="Progress">
      <div class="tab-content">
        <h2>Progress Indicators</h2>
        <p class="design-description">
          Material components for showing progress and loading states.
        </p>

        <section class="design-section">
          <h3>Progress Bars</h3>
          <p class="design-description">
            Linear progress indicators for showing completion or loading states.
          </p>

          <h4>Determinate Progress (with value)</h4>
          <div class="component-column mb-3">
            <p>Primary Color - 60%</p>
            <mat-progress-bar mode="determinate" value="60" color="primary"></mat-progress-bar>

            <p class="mt-3">Accent Color - 75%</p>
            <mat-progress-bar mode="determinate" value="75" color="accent"></mat-progress-bar>

            <p class="mt-3">Warn Color - 40%</p>
            <mat-progress-bar mode="determinate" value="40" color="warn"></mat-progress-bar>

            <p class="mt-3">Default Color - 85%</p>
            <mat-progress-bar mode="determinate" value="85"></mat-progress-bar>
          </div>

          <h4>Indeterminate Progress (loading)</h4>
          <div class="component-column mb-3">
            <p>Primary Color</p>
            <mat-progress-bar mode="indeterminate" color="primary"></mat-progress-bar>

            <p class="mt-3">Accent Color</p>
            <mat-progress-bar mode="indeterminate" color="accent"></mat-progress-bar>
          </div>

          <h4 class="mt-4">HTML Code</h4>
          <pre class="code-block"><code>&lt;mat-progress-bar mode="determinate" value="60" color="primary"&gt;&lt;/mat-progress-bar&gt;

&lt;mat-progress-bar mode="indeterminate" color="primary"&gt;&lt;/mat-progress-bar&gt;</code></pre>
        </section>

        <section class="design-section">
          <h3>Progress Spinners</h3>
          <p class="design-description">
            Circular progress indicators for loading states. Always add <code>color="primary"</code> for brand consistency.
          </p>

          <h4>Standard Spinners</h4>
          <div class="component-row mb-3" style="align-items: center;">
            <div style="display: flex; flex-direction: column; align-items: center; margin-right: 2rem;">
              <mat-spinner color="primary" diameter="50"></mat-spinner>
              <p class="mt-2">Primary (50px)</p>
            </div>
            <div style="display: flex; flex-direction: column; align-items: center; margin-right: 2rem;">
              <mat-spinner color="accent" diameter="50"></mat-spinner>
              <p class="mt-2">Accent (50px)</p>
            </div>
            <div style="display: flex; flex-direction: column; align-items: center; margin-right: 2rem;">
              <mat-spinner color="warn" diameter="50"></mat-spinner>
              <p class="mt-2">Warn (50px)</p>
            </div>
          </div>

          <h4>Small Spinners (for inline use)</h4>
          <div class="component-row mb-3" style="align-items: center;">
            <mat-spinner color="primary" diameter="24"></mat-spinner>
            <span class="ml-3">24px spinner for inline loading</span>
          </div>

          <h4 class="mt-4">HTML Code</h4>
          <pre class="code-block"><code>&lt;mat-spinner color="primary"&gt;&lt;/mat-spinner&gt;

&lt;mat-spinner color="primary" diameter="24"&gt;&lt;/mat-spinner&gt;</code></pre>
        </section>

        <section class="design-section">
          <h3>Best Practices</h3>

          <h4>DO's ✅</h4>
          <ul class="notes-list">
            <li>Use <code>mode="determinate"</code> when progress percentage is known</li>
            <li>Use <code>mode="indeterminate"</code> for unknown duration loading</li>
            <li>Always add <code>color="primary"</code> for brand consistency</li>
            <li>Place progress bars prominently where users expect feedback</li>
            <li>Always use <code>color="primary"</code> for spinners</li>
            <li>Use standard size (default) for full-page loading</li>
            <li>Use small spinners (<code>diameter="24"</code>) for inline loading</li>
            <li>Center spinners in their container for better UX</li>
          </ul>

          <h4>DON'Ts ❌</h4>
          <ul class="notes-list">
            <li>Don't use progress indicators without user context</li>
            <li>Don't use spinners for known-duration operations (use progress bars instead)</li>
            <li>Don't forget to add loading state accessibility labels</li>
            <li>Don't use multiple spinners simultaneously in the same view</li>
          </ul>
        </section>

      </div>
    </mat-tab>

    <!-- Chips Tab -->
    <mat-tab label="Chips">
      <div class="tab-content">
        <h2>Chips Components</h2>
        <p class="design-description">
          Compact elements representing inputs, attributes, or actions. Use chips for tags, categories, filters, or selections.
        </p>

        <section class="design-section">
          <h3>Standard Chips</h3>
          <p class="design-description">
            Basic chips with theme colors. Use <code>highlighted</code> attribute with <code>color</code> for emphasis.
          </p>

          <div class="component-row mb-3">
            <mat-chip-set>
              <mat-chip color="primary" highlighted>Primary Chip</mat-chip>
              <mat-chip color="accent" highlighted>Accent Chip</mat-chip>
              <mat-chip>Default Chip</mat-chip>
              <mat-chip disabled>Disabled Chip</mat-chip>
            </mat-chip-set>
          </div>

          <h4 class="mt-4">HTML Code</h4>
          <pre class="code-block"><code>&lt;mat-chip-set&gt;
  &lt;mat-chip color="primary" highlighted&gt;Primary Chip&lt;/mat-chip&gt;
  &lt;mat-chip color="accent" highlighted&gt;Accent Chip&lt;/mat-chip&gt;
  &lt;mat-chip&gt;Default Chip&lt;/mat-chip&gt;
  &lt;mat-chip disabled&gt;Disabled Chip&lt;/mat-chip&gt;
&lt;/mat-chip-set&gt;</code></pre>
        </section>

        <section class="design-section">
          <h3>Removable Chips</h3>
          <p class="design-description">
            Chips with remove buttons for user-added content like tags or filters.
          </p>

          <div class="component-row mb-3">
            <mat-chip-set>
              <mat-chip color="primary" highlighted>
                Tag 1
                <button matChipRemove>
                  <mat-icon>cancel</mat-icon>
                </button>
              </mat-chip>
              <mat-chip color="primary" highlighted>
                Tag 2
                <button matChipRemove>
                  <mat-icon>cancel</mat-icon>
                </button>
              </mat-chip>
              <mat-chip color="primary" highlighted>
                Tag 3
                <button matChipRemove>
                  <mat-icon>cancel</mat-icon>
                </button>
              </mat-chip>
            </mat-chip-set>
          </div>

          <h4 class="mt-4">HTML Code</h4>
          <pre class="code-block"><code>&lt;mat-chip color="primary" highlighted&gt;
  Tag 1
  &lt;button matChipRemove&gt;
    &lt;mat-icon&gt;cancel&lt;/mat-icon&gt;
  &lt;/button&gt;
&lt;/mat-chip&gt;</code></pre>
        </section>

        <section class="design-section">
          <h3>Chips with Icons</h3>
          <p class="design-description">
            Chips can include icons for additional visual context.
          </p>

          <div class="component-row mb-3">
            <mat-chip-set>
              <mat-chip color="primary" highlighted>
                <mat-icon>person</mat-icon>
                User
              </mat-chip>
              <mat-chip color="accent" highlighted>
                <mat-icon>star</mat-icon>
                Featured
              </mat-chip>
              <mat-chip color="primary" highlighted>
                <mat-icon>label</mat-icon>
                Category
              </mat-chip>
            </mat-chip-set>
          </div>

          <h4 class="mt-4">HTML Code</h4>
          <pre class="code-block"><code>&lt;mat-chip color="primary" highlighted&gt;
  &lt;mat-icon&gt;person&lt;/mat-icon&gt;
  User
&lt;/mat-chip&gt;</code></pre>
        </section>

        <section class="design-section">
          <h3>Best Practices</h3>

          <h4>DO's ✅</h4>
          <ul class="notes-list">
            <li>Use chips for tags, categories, or filters</li>
            <li>Add <code>highlighted</code> attribute with color for emphasized chips</li>
            <li>Use removable chips for user-added content</li>
            <li>Keep chip text short and descriptive (1-3 words)</li>
            <li>Use icons in chips to provide visual context</li>
            <li>Group related chips in <code>mat-chip-set</code></li>
          </ul>

          <h4>DON'Ts ❌</h4>
          <ul class="notes-list">
            <li>Don't use chips for long text content (use cards instead)</li>
            <li>Don't create custom chip styles without updating the design guideline</li>
            <li>Don't use chips as primary action buttons (use buttons instead)</li>
            <li>Don't overuse removable chips - only for user-added items</li>
            <li>Don't use chips without proper context or labels</li>
          </ul>
        </section>

      </div>
    </mat-tab>

    <!-- Dialogs Tab -->
    <mat-tab label="Dialogs">
      <div class="tab-content">
        <h2>Dialog Patterns</h2>
        <p class="design-description">
          Angular Material dialogs following the LMC design guideline with flat design principles and brand color theming.
          Every example below is live: open it, and read the code beneath it.
        </p>
        <p class="design-description">
          <strong>Every dialog has the same shape:</strong> a title row carrying a close cross, and an
          actions bar only when there is a real decision or a real primary action to put in it. A button
          whose only job is to close duplicates the cross and is left out.
        </p>
        <div class="dialog-shape-table">
          <table class="flat-table">
            <thead>
              <tr><th>Pattern</th><th>Actions bar</th><th>Why</th></tr>
            </thead>
            <tbody>
              <tr><td>Confirmation</td><td><code>Cancel</code> + <code>Confirm</code></td><td>Cancel is an answer ("no"), not a close</td></tr>
              <tr><td>Alert</td><td>&mdash;</td><td>Acknowledge-only; the cross acknowledges</td></tr>
              <tr><td>Form</td><td><code>Save</code></td><td>The cross discards, Save commits</td></tr>
              <tr><td>Information</td><td>&mdash;</td><td>Nothing to decide</td></tr>
              <tr><td>Tool</td><td>&mdash;</td><td>A working surface</td></tr>
            </tbody>
          </table>
        </div>
        <p class="design-description">
          The cross returns the same value the removed button would have: <code>false</code> on a
          Confirmation, <code>null</code> on the Form. Dismissing is never ambiguous.
        </p>

        <!-- Confirmation Dialog -->
        <section class="design-section" id="confirmation-dialog">
          <h3>Confirmation Dialog</h3>
          <p class="design-description">
            A yes/no question: an action that needs an explicit answer (delete, submit, approve).
            <strong>The only pattern that keeps a Cancel button</strong> &mdash; Cancel is not a
            duplicate of the cross, it is the other half of the question, and putting the two
            answers side by side is what makes the dialog read as a question. The cross returns the
            same <code>false</code> as Cancel, so dismissing is never mistaken for consent.
          </p>

          <button mat-raised-button color="primary" (click)="openConfirmationDialog()">
            Open Confirmation Dialog
          </button>

          <h4 class="mt-4">HTML Code</h4>
          <pre class="code-block"><code>&lt;div class="dialog-header"&gt;
  &lt;h2 mat-dialog-title class="lmc-dialog-title dialog-title-icon"&gt;&lt;mat-icon color="primary"&gt;help_outline&lt;/mat-icon&gt;
    Confirm Action
  &lt;/h2&gt;
  &lt;button mat-icon-button [mat-dialog-close]="false" type="button"
          class="close-button" aria-label="Dismiss without confirming" matTooltip="Close"&gt;
    &lt;mat-icon&gt;close&lt;/mat-icon&gt;
  &lt;/button&gt;
&lt;/div&gt;

&lt;mat-dialog-content&gt;
  &lt;p&gt;Are you sure you want to proceed?&lt;/p&gt;
&lt;/mat-dialog-content&gt;

&lt;mat-dialog-actions align="end"&gt;
  &lt;button mat-button [mat-dialog-close]="false"&gt;Cancel&lt;/button&gt;
  &lt;button mat-button color="primary" [mat-dialog-close]="true"&gt;Confirm&lt;/button&gt;
&lt;/mat-dialog-actions&gt;</code></pre>
        </section>

        <!-- Alert Dialog -->
        <section class="design-section">
          <h3>Alert Dialog</h3>
          <p class="design-description">
            Something the operator needs to see before carrying on. Warning icon, title in the
            warning colour, no actions bar &mdash; the old &ldquo;I Understand&rdquo; button did nothing
            the cross does not.
          </p>
          <p class="design-description">
            <strong>Note:</strong> this no longer forces an acknowledgement. If a particular
            warning genuinely must be clicked through, make it a
            <a href="#confirmation-dialog">Confirmation</a> instead, where the answer is recorded
            and acted on.
          </p>

          <button mat-raised-button color="warn" (click)="openAlertDialog()">
            Open Alert Dialog
          </button>

          <h4 class="mt-4">HTML Code</h4>
          <pre class="code-block"><code>&lt;div class="dialog-header"&gt;
  &lt;h2 mat-dialog-title class="lmc-dialog-title dialog-title-icon dialog-title-warn"&gt;
    &lt;mat-icon color="warn"&gt;warning&lt;/mat-icon&gt;
    Warning
  &lt;/h2&gt;
  &lt;button mat-icon-button mat-dialog-close type="button"
          class="close-button" aria-label="Close warning" matTooltip="Close"&gt;
    &lt;mat-icon&gt;close&lt;/mat-icon&gt;
  &lt;/button&gt;
&lt;/div&gt;

&lt;mat-dialog-content&gt;
  &lt;p&gt;This action cannot be undone.&lt;/p&gt;
&lt;/mat-dialog-content&gt;

&lt;!-- No &lt;mat-dialog-actions&gt;: nothing to decide. --&gt;</code></pre>
        </section>

        <!-- Form Dialog -->
        <section class="design-section">
          <h3>Form Dialog</h3>
          <p class="design-description">
            Collects input and returns it. The actions bar holds <strong>Save alone</strong> &mdash;
            Cancel closed and returned null, which is exactly what the cross does. Save stays
            disabled until the form is valid, so the dialog cannot hand back something it knows
            is wrong.
          </p>

          <button mat-raised-button color="primary" (click)="openFormDialog()">
            Open Form Dialog
          </button>

          <h4 class="mt-4">HTML Code</h4>
          <pre class="code-block"><code>&lt;div class="dialog-header"&gt;
  &lt;h2 mat-dialog-title class="lmc-dialog-title"&gt;Edit Application&lt;/h2&gt;
  &lt;button mat-icon-button [mat-dialog-close]="null" type="button"
          class="close-button" aria-label="Discard changes and close" matTooltip="Close"&gt;
    &lt;mat-icon&gt;close&lt;/mat-icon&gt;
  &lt;/button&gt;
&lt;/div&gt;

&lt;mat-dialog-content&gt;
  &lt;mat-form-field appearance="outline"&gt;
    &lt;mat-label&gt;Name&lt;/mat-label&gt;
    &lt;input matInput required&gt;
  &lt;/mat-form-field&gt;

  &lt;mat-form-field appearance="outline"&gt;
    &lt;mat-label&gt;Email&lt;/mat-label&gt;
    &lt;input matInput type="email" required&gt;
  &lt;/mat-form-field&gt;

  &lt;mat-form-field appearance="outline"&gt;
    &lt;mat-label&gt;Amount&lt;/mat-label&gt;
    &lt;input matInput type="number" required&gt;
    &lt;span matPrefix&gt;$&amp;nbsp;&lt;/span&gt;
  &lt;/mat-form-field&gt;
&lt;/mat-dialog-content&gt;

&lt;!-- Save only. The cross is the way out; a Cancel beside it would be the same button. --&gt;
&lt;mat-dialog-actions align="end"&gt;
  &lt;button mat-button color="primary" [disabled]="form.invalid"&gt;Save&lt;/button&gt;
&lt;/mat-dialog-actions&gt;</code></pre>
        </section>

        <!-- Information Dialog -->
        <section class="design-section" id="information-dialog">
          <h3>Information Dialog</h3>
          <p class="design-description">
            Read-only detail. Nothing to decide, so no actions bar &mdash; the cross is the only
            control. The line between this and a <a href="#tool-dialog">Tool Dialog</a> is length:
            a handful of facts that fit without scrolling belong here; a surface the operator
            works in belongs there.
          </p>

          <button mat-raised-button color="primary" (click)="openInfoDialog()">
            Open Info Dialog
          </button>

          <h4 class="mt-4">HTML Code</h4>
          <pre class="code-block"><code>&lt;div class="dialog-header"&gt;
  &lt;h2 mat-dialog-title class="lmc-dialog-title dialog-title-icon"&gt;
    &lt;mat-icon color="primary"&gt;info&lt;/mat-icon&gt;
    Application Details
  &lt;/h2&gt;
  &lt;button mat-icon-button mat-dialog-close type="button"
          class="close-button" aria-label="Close application details" matTooltip="Close"&gt;
    &lt;mat-icon&gt;close&lt;/mat-icon&gt;
  &lt;/button&gt;
&lt;/div&gt;

&lt;mat-dialog-content&gt;
  &lt;div class="dialog-info-row"&gt;
    &lt;span class="label"&gt;Application ID:&lt;/span&gt;
    &lt;span class="value"&gt;LA-2024-001234&lt;/span&gt;
  &lt;/div&gt;
  &lt;div class="dialog-info-row"&gt;
    &lt;span class="label"&gt;Applicant:&lt;/span&gt;
    &lt;span class="value"&gt;John Doe&lt;/span&gt;
  &lt;/div&gt;
  &lt;div class="dialog-info-row"&gt;
    &lt;span class="label"&gt;Amount:&lt;/span&gt;
    &lt;span class="value"&gt;$50,000&lt;/span&gt;
  &lt;/div&gt;
  &lt;div class="dialog-info-row"&gt;
    &lt;span class="label"&gt;Status:&lt;/span&gt;
    &lt;span class="value"&gt;
      &lt;span class="status-badge status-approved"&gt;Approved&lt;/span&gt;
    &lt;/span&gt;
  &lt;/div&gt;
&lt;/mat-dialog-content&gt;

&lt;!-- No &lt;mat-dialog-actions&gt;: the cross is the only action. --&gt;</code></pre>
        </section>

        <!-- Tool Dialog -->
        <section class="design-section" id="tool-dialog">
          <h3>Tool Dialog</h3>
          <p class="design-description">
            A working surface the operator opens, uses and dismisses &mdash; a search, a lookup, a
            read-only report. Nothing to accept or cancel, so no actions bar. What sets it apart from
            an <a href="#information-dialog">Information Dialog</a> is that the operator <em>works</em>
            in it: a fixed height so results arriving do not resize the frame, one inner scroller, and
            placeholders sharing a single centred slot.
          </p>
          <p class="design-description">
            Reference implementation:
            <code>loan-servicing/payment-processing/search/transaction-search-dialog.component.*</code>
          </p>

          <button mat-raised-button color="primary" (click)="openToolDialog()">
            Open Tool Dialog
          </button>

          <h4 class="mt-4">HTML Code</h4>
          <pre class="code-block"><code>&lt;div class="dialog-header"&gt;
  &lt;h2 mat-dialog-title class="lmc-dialog-title"&gt;Find an Applicant&lt;/h2&gt;
  &lt;button mat-icon-button mat-dialog-close type="button"
          class="close-button"
          aria-label="Close applicant search"
          matTooltip="Close"&gt;
    &lt;mat-icon&gt;close&lt;/mat-icon&gt;
  &lt;/button&gt;
&lt;/div&gt;

&lt;mat-dialog-content class="dialog-content"&gt;
  &lt;!-- control surface (search field, filters) --&gt;
  &lt;div class="results-area"&gt;
    &lt;!-- results, or a centred placeholder --&gt;
  &lt;/div&gt;
&lt;/mat-dialog-content&gt;

&lt;!-- No &lt;mat-dialog-actions&gt;: the cross above is the only action. --&gt;</code></pre>

          <h4 class="mt-4">SCSS &mdash; the two things you must handle</h4>
          <p class="design-description">
            Both are traps rather than choices. Skip either and the dialog looks wrong in a way
            that is hard to trace back to its cause.
          </p>
          <pre class="code-block"><code>// 1. The dialog surface has NO padding of its own. _dialogs.scss puts the inset
//    on the title and content elements, and this header row is neither &mdash; so
//    without its own padding the cross lands jammed in the top-right corner.
//    Right is 1rem, not 1.5rem: an icon button carries ~8px around its 24px
//    glyph, so 16px here puts the glyph's edge on the content's 24px line.
.dialog-header {{'{'}}
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.5rem 1rem 0 0;

  // 2. `align-items: center` centres the BOXES, and MDC inflates the title's box
  //    without moving its text:
  //      .mdc-dialog__title::before {{'{'}} display:inline-block; width:0;
  //                                   height:40px; content:""; vertical-align:0; {{'}'}}
  //    That strut pushes the line box 40px above the baseline and leaves the text
  //    at the BOTTOM of a ~47px box &mdash; about 10px below the glyph's centre.
  //    Drop it and both centres land at 20px.
  .lmc-dialog-title {{'{'}}
    padding-bottom: 0;
    &amp;::before {{'{'}} display: none; {{'}'}}
  {{'}'}}

  .close-button {{'{'}} flex-shrink: 0; color: #666; {{'}'}}
{{'}'}}

// A fixed height, not a max: content loading in must not resize the dialog.
// No actions bar follows, so only the header is subtracted.
.dialog-content {{'{'}}
  display: flex;
  flex-direction: column;
  padding-top: 0.5rem !important;
  padding-bottom: 0.5rem !important;
  height: calc(70vh - 96px);
  max-height: none;
  overflow: hidden;
{{'}'}}

// The one scroller. min-height: 0 is what lets a flex child shrink below its
// content and actually scroll.
.results-area {{'{'}}
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
{{'}'}}</code></pre>

          <h4 class="mt-4">Why <code>_dialogs.scss</code> cannot help here</h4>
          <p class="design-description">
            Its <code>h2[mat-dialog-title] {{ '{' }} padding: 0 24px 16px {{ '}' }}</code> rule sits inside a
            <code>::ng-deep</code> block in a <strong>global</strong> stylesheet (imported at
            <code>styles.scss:28</code>), where <code>::ng-deep</code> reaches the browser as an
            unknown pseudo-element and invalidates the selector &mdash; the file&rsquo;s own comment
            says so. Every dialog title in the app is therefore styled by MDC&rsquo;s rules, not that
            block. <code>.lmc-dialog-title</code> is declared outside the block and does apply, but
            it carries typography only and leaves layout to each dialog &mdash; which is why every
            example on this page sets its own title row.
          </p>
        </section>

        <!-- Best Practices -->
        <section class="design-section">
          <h3>Dialog Best Practices</h3>

          <h4>DO's ✅</h4>
          <ul class="notes-list">
            <li>Use <code>&lt;h2 mat-dialog-title&gt;</code> for dialog titles (Material Design standard)</li>
            <li>Use <code>&lt;mat-dialog-content&gt;</code> and <code>&lt;mat-dialog-actions&gt;</code> (Material standard)</li>
            <li>Use <code>inject()</code> function for dependency injection (modern Angular)</li>
            <li>Use <code>mat-button</code> for flat button style in dialogs</li>
            <li>Use <code>align="end"</code> for right-aligned dialog actions</li>
            <li>Use <code>width: '400px'</code> for confirmation/alert dialogs</li>
            <li>Use <code>width: '600px'</code> for form dialogs with multiple fields</li>
            <li>Always include <code>maxWidth: '90vw'</code> for mobile responsiveness</li>
            <li>Always provide a way to close the dialog (Cancel button or mat-dialog-close)</li>
            <li>Include icons in dialog titles for better visual communication</li>
            <li>Disable action button when form is invalid</li>
            <li>Apply <code>.lmc-dialog-title</code> to every <code>&lt;h2 mat-dialog-title&gt;</code> &mdash; it is the shared title typography, and MDC's default is not it</li>
            <li>Set the title row's layout in the dialog itself (the flex row for an icon, or for a close cross) &mdash; <code>_dialogs.scss</code> does not supply it</li>
            <li><strong>Give every dialog a close cross in the title</strong> &mdash; it is the one dismissal every dialog has</li>
            <li>Add an actions bar only for a real decision (Confirmation) or a real primary action (Save) &mdash; never for a button that only closes</li>
            <li>Make the cross return what the removed button would have returned (<code>false</code>, <code>null</code>), so dismissing is unambiguous</li>
            <li>Give the cross an <code>aria-label</code> &mdash; the glyph alone is not a name</li>
            <li>Put <code>cdkFocusInitial</code> on the control the operator came to use, and leave <code>autoFocus</code> at its default so the focus trap honours it</li>
            <li>Report progress and failure in the content area, not on the input that triggered them</li>
          </ul>

          <h4>DON'Ts ❌</h4>
          <ul class="notes-list">
            <li>Don't use <code>&lt;h1&gt;</code> or <code>&lt;h3&gt;</code> - use <code>&lt;h2 mat-dialog-title&gt;</code> (Material standard)</li>
            <li>Don't use <code>mat-raised-button</code> or <code>mat-flat-button</code> in dialogs - use <code>mat-button</code></li>
            <li>Don't forget <code>align="end"</code> on mat-dialog-actions for right-aligned buttons</li>
            <li>Don't use constructor injection - prefer <code>inject()</code> function</li>
            <li>Don't create dialogs wider than <code>800px</code> - use a full page instead</li>
            <li>Don't stack multiple dialogs - close current before opening new one</li>
            <li>Don't use dialogs for complex workflows - use a wizard or multi-step form</li>
            <li>Don't forget to handle the <code>afterClosed()</code> subscription</li>
            <li>Don't put a Close / Cancel button beside a close cross &mdash; the exception is a Confirmation, where Cancel is the other half of the question</li>
            <li>Don't rely on an Alert's button to force acknowledgement &mdash; it no longer has one; use a Confirmation when the answer must be recorded</li>
            <li>Don't expect <code>align-items: center</code> to centre a dialog title &mdash; MDC's <code>::before</code> strut leaves the text at the bottom of its box; disable it when the title shares a row</li>
            <li>Don't rely on the <code>::ng-deep</code> rules in <code>_dialogs.scss</code> &mdash; they are in a global stylesheet and never reach the DOM</li>
            <li>Don't pass <code>autoFocus: false</code> and then wonder why <code>cdkFocusInitial</code> is ignored</li>
            <li>Don't use <code>type="search"</code> on an input &mdash; WebKit adds a second clear (x) beside the Material one</li>
          </ul>

          <h4>Configuration Options</h4>
          <pre class="code-block"><code>const dialogRef = this.dialog.open(MyDialogComponent, {{'{'}}
  width: '400px',           // Dialog width
  maxWidth: '90vw',         // Maximum width (responsive)
  minHeight: '200px',       // Minimum height
  disableClose: true,       // Prevent closing on backdrop click
  autoFocus: true,          // Auto-focus first input
  panelClass: 'custom-dialog', // Custom CSS class
  data: {{'{'}} /* data */ {{'}'}}   // Data to pass to dialog
{{'}'}});</code></pre>
        </section>

      </div>
    </mat-tab>

    <mat-tab label="Status Breakdown">
      <div class="tab-content">
        <h2>Status Breakdown</h2>
        <p class="design-description">
          <code>&lt;lmc-status-breakdown&gt;</code> summarises a set of status counts as a stacked
          proportion bar plus an aligned legend. Numbers use tabular figures and the legend is a
          fixed-column grid, so counts stay aligned across table rows regardless of digit width.
          It is fully data-driven and palette-agnostic — pass <code>items</code> (label + count +
          color) and, optionally, a <code>total</code>.
        </p>

        <section class="design-section">
          <h3>Example</h3>
          <p class="design-description">
            A per-status summary as used in Payment Processing. Zero-count statuses stay visible
            but dimmed; hover a bar segment for its exact count.
          </p>

          <div class="component-row" style="align-items: flex-start;">
            <lmc-status-breakdown
              [items]="statusBreakdownItems"
              [total]="128">
            </lmc-status-breakdown>
          </div>

          <h4 class="mt-4">TypeScript</h4>
          <pre class="code-block"><code>import {{'{'}}
  StatusBreakdownComponent,
  StatusBreakdownItem,
{{'}'}} from '&#64;lmc-app/shared/components/status-breakdown/status-breakdown.component';

// Add StatusBreakdownComponent to the standalone component's `imports`
// (or the NgModule `imports` for a module-declared component).

items: StatusBreakdownItem[] = [
  {{'{'}} label: 'Queued',     count: 12, color: '#7986cb' {{'}'}},
  {{'{'}} label: 'Accepted',   count: 95, color: '#4caf50' {{'}'}},
  {{'{'}} label: 'Returned',   count: 4,  color: '#e57373' {{'}'}},
  {{'{'}} label: 'Error',      count: 1,  color: '#d32f2f' {{'}'}},
];</code></pre>

          <h4 class="mt-4">HTML Code</h4>
          <pre class="code-block"><code>&lt;lmc-status-breakdown
  [items]="items"
  [total]="128"&gt;
&lt;/lmc-status-breakdown&gt;</code></pre>
        </section>

        <section class="design-section">
          <h3>Inputs</h3>

          <div class="info-row mb-3">
            <span class="label"><code>items</code></span>
            <span class="value"><code>StatusBreakdownItem[]</code> — each <code>{{ '{' }} label, count, color {{ '}' }}</code>. Required.</span>
          </div>
          <div class="info-row mb-3">
            <span class="label"><code>total</code></span>
            <span class="value"><code>number | null</code> — headline total. When null, the item counts are summed. Default <code>null</code>.</span>
          </div>
          <div class="info-row mb-3">
            <span class="label"><code>totalLabel</code></span>
            <span class="value"><code>string</code> — label beside the total. Default <code>'Total'</code>.</span>
          </div>
          <div class="info-row mb-3">
            <span class="label"><code>showTotal</code></span>
            <span class="value"><code>boolean</code> — render the headline total row. Default <code>true</code>.</span>
          </div>
          <div class="info-row mb-3">
            <span class="label"><code>showZeros</code></span>
            <span class="value"><code>boolean</code> — show zero-count rows in the legend (dimmed). The bar always excludes zeros. Default <code>true</code>.</span>
          </div>
          <div class="info-row mb-3">
            <span class="label"><code>--sb-legend-min</code></span>
            <span class="value">CSS custom property — legend column width. The legend fits as many
              columns as the container allows and wraps the rest. Default <code>140px</code>.</span>
          </div>
        </section>

        <section class="design-section">
          <h3>Guidelines</h3>

          <h4>DO's ✅</h4>
          <ul class="notes-list">
            <li>Use it for any per-status / per-category count summary (batches, tasks, applications)</li>
            <li>Draw colors from the LMC palette in <code>_variables.scss</code> for consistency</li>
            <li>Pass an explicit <code>total</code> when it can differ from the sum of shown statuses</li>
          </ul>

          <h4>DON'Ts ❌</h4>
          <ul class="notes-list">
            <li>Don't hardcode the same palette in multiple places - define it once per feature</li>
            <li>Don't use it for a single value - a plain badge or metric card is clearer</li>
          </ul>
        </section>
      </div>
    </mat-tab>

  </mat-tab-group>

</div>
